import { useState } from 'react';
import { Phone, Mail, MapPin, Send, Award, Check, Facebook, Instagram, Linkedin, MessageCircle, Loader2 } from 'lucide-react';
import Footer from '../../components/Footer';
import { useSiteTooling } from '../../hooks/useSiteTooling';

const Contact = () => {
  const { trackEvent } = useSiteTooling();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    enquiry: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Try Vercel API first
      let response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      // If Vercel fails, fallback to Render
      if (!response.ok) {
        response = await fetch('https://molomo-email-service.onrender.com/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
      }

      const data = await response.json();

      if (data.success) {
        trackEvent('form_submit', { form_name: 'contact_form', enquiry_type: formData.enquiry, success: true });
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', company: '', email: '', phone: '', enquiry: '', message: '' });
        }, 5000);
      } else {
        trackEvent('form_submit', { form_name: 'contact_form', success: false });
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      trackEvent('form_submit', { form_name: 'contact_form', success: false });
      console.error('Error sending email:', err);
      setError('Failed to send message. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const hasStartedForm = useState(false);

  const handleInputChange = (e) => {
    if (!hasStartedForm[0] && e.target.value) {
      hasStartedForm[1](true);
      trackEvent('form_start', { form_name: 'contact_form' });
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="w-full bg-charcoal py-16 lg:py-24">
        <div className="px-6 lg:px-[7vw]">
          <h1 className="font-display font-bold text-white text-4xl lg:text-6xl mb-4">
            Contact Us
          </h1>
          <p className="text-white/60 max-w-2xl text-lg">
            Get in touch for a free quote. We'll help you find the perfect storage solution.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="w-full bg-offwhite py-12 lg:py-20">
        <div className="px-6 lg:px-[7vw]">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            {/* Left - Contact Info */}
            <div className="w-full lg:w-2/5">
              <h2 className="font-display font-bold text-charcoal text-2xl mb-4">
                Let's build your space.
              </h2>
              <p className="text-charcoal/70 mb-8">
                Are you searching for the perfect racking, shelving, storage, or mezzanine solution? 
                We're here to help! Contact us with any questions or enquiries.
              </p>

              <div className="space-y-4 mb-8">
                <a href="tel:+27829713299" className="flex items-center gap-3 text-charcoal hover:text-charcoal/80">
                  <div className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium">+27 82 971 3299</p>
                    <p className="text-sm text-charcoal/50">Mon-Fri, 8am-5pm</p>
                  </div>
                </a>
                <a href="mailto:nhlapolindiwe4@gmail.com" className="flex items-center gap-3 text-charcoal hover:text-charcoal/80">
                  <div className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium">nhlapolindiwe4@gmail.com</p>
                  </div>
                </a>
                <a 
                  href="https://www.google.com/maps/search/7323+Zone+12+ext+Solomon+Mahlangu+Street+Sebokeng+1983"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-charcoal hover:text-charcoal/80"
                >
                  <div className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium">7323 Zone 12 ext</p>
                    <p className="text-sm text-charcoal/50">Solomon Mahlangu Street, Sebokeng 1983</p>
                  </div>
                </a>
              </div>

              <div className="flex items-center gap-3 bg-charcoal/5 rounded-xl p-4 mb-6">
                <Award className="w-10 h-10 text-lime" />
                <div>
                  <p className="font-medium text-charcoal">B-BBEE Level 1</p>
                  <p className="text-sm text-charcoal/60">100% black-owned</p>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-3">
                <a href="https://api.whatsapp.com/send?phone=27829713299" target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp" className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center text-charcoal/60 hover:bg-lime hover:text-charcoal transition-colors">
                  <MessageCircle className="w-5 h-5" />
                </a>
                <a href="https://www.facebook.com/molomonr" target="_blank" rel="noopener noreferrer" aria-label="Visit our Facebook page" className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center text-charcoal/60 hover:bg-lime hover:text-charcoal transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/molomonr" target="_blank" rel="noopener noreferrer" aria-label="Visit our Instagram page" className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center text-charcoal/60 hover:bg-lime hover:text-charcoal transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.linkedin.com/company/molomonr" target="_blank" rel="noopener noreferrer" aria-label="Visit our LinkedIn page" className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center text-charcoal/60 hover:bg-lime hover:text-charcoal transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Right - Form */}
            <div className="w-full lg:w-3/5">
              <div className="bg-white rounded-2xl p-6 lg:p-10 shadow-sm">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-lime/20 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-lime" />
                    </div>
                    <h3 className="font-display font-bold text-charcoal text-xl mb-2">Message sent!</h3>
                    <p className="text-charcoal/70">One of our agents will be in touch shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <h3 className="font-display font-bold text-charcoal text-lg mb-2">For more insight drop us an email.</h3>
                      <p className="text-charcoal/60 text-sm">One of our agents will be in touch shortly.</p>
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                        {error}
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-charcoal">Name *</label>
                        <input name="name" value={formData.name} onChange={handleInputChange} required placeholder="Enter your name" className="w-full h-12 px-4 rounded-lg border border-charcoal/20 bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lime autofill:bg-white autofill:text-charcoal" style={{ WebkitBoxShadow: '0 0 0 30px white inset' }} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-charcoal">Company</label>
                        <input name="company" value={formData.company} onChange={handleInputChange} placeholder="Enter your company" className="w-full h-12 px-4 rounded-lg border border-charcoal/20 bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lime" style={{ WebkitBoxShadow: '0 0 0 30px white inset' }} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-charcoal">Email *</label>
                        <input name="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="Enter your email" className="w-full h-12 px-4 rounded-lg border border-charcoal/20 bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lime" style={{ WebkitBoxShadow: '0 0 0 30px white inset' }} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-charcoal">Phone</label>
                        <input name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+27 ..." className="w-full h-12 px-4 rounded-lg border border-charcoal/20 bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lime" style={{ WebkitBoxShadow: '0 0 0 30px white inset' }} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-charcoal">Enquiry type</label>
                      <select name="enquiry" value={formData.enquiry} onChange={handleInputChange} className="w-full h-12 px-4 rounded-lg border border-charcoal/20 bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lime">
                        <option value="">Select enquiry type</option>
                        <option value="racking">Pallet Racking</option>
                        <option value="shelving">Industrial Shelving</option>
                        <option value="mezzanine">Mezzanine Floors</option>
                        <option value="storage">Lockers & Storage</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-charcoal">Message</label>
                      <textarea name="message" value={formData.message} onChange={handleInputChange} rows={4} placeholder="Enter your message..." className="w-full px-4 py-3 rounded-lg border border-charcoal/20 bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lime resize-none" style={{ WebkitBoxShadow: '0 0 0 30px white inset' }} />
                    </div>

                    <button 
                      type="submit" 
                      disabled={isLoading}
                      className="w-full h-12 bg-lime text-charcoal hover:bg-lime/90 font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
